/**
 * IndexedDB. One object store per entity, so a screen reads only what it needs
 * and nothing waits on a spinner after the first sync (CLAUDE.md §10).
 *
 * Settings live here too — but NEVER key material. The derived key stays in
 * memory for the session; see key.ts for why localStorage is out entirely.
 */
import { openDB } from "idb";
import type { DBSchema, IDBPDatabase } from "idb";
import type { Snapshot, Voucher } from "@tally/shared";

const DB_NAME = "tally-pocket";
const DB_VERSION = 1;

type StoredSnapshot = {
  companyId: string;
  hash: string;
  generatedAt: string;
  /** when THIS device pulled it — the basis for "synced N minutes ago" */
  syncedAt: string;
  snapshot: Snapshot;
};

interface TallyDB extends DBSchema {
  snapshots: {
    key: string;
    value: StoredSnapshot;
  };
  vouchers: {
    key: string;
    value: Voucher & { companyId: string };
    indexes: {
      "by-company-date": [string, string];
      "by-company-type": [string, string];
      "by-company-party": [string, string];
    };
  };
  settings: {
    key: string;
    value: unknown;
  };
}

let dbPromise: Promise<IDBPDatabase<TallyDB>> | null = null;

export function db(): Promise<IDBPDatabase<TallyDB>> {
  dbPromise ??= openDB<TallyDB>(DB_NAME, DB_VERSION, {
    upgrade(database) {
      database.createObjectStore("snapshots", { keyPath: "companyId" });

      const vouchers = database.createObjectStore("vouchers", { keyPath: "id" });
      vouchers.createIndex("by-company-date", ["companyId", "date"]);
      vouchers.createIndex("by-company-type", ["companyId", "voucherType"]);
      vouchers.createIndex("by-company-party", ["companyId", "partyLedger"]);

      database.createObjectStore("settings");
    },
  });
  return dbPromise;
}

export async function saveSnapshot(
  companyId: string,
  snapshot: Snapshot,
  hash: string,
  syncedAt: string,
): Promise<void> {
  const database = await db();

  const tx = database.transaction(["snapshots", "vouchers"], "readwrite");
  await tx.objectStore("snapshots").put({
    companyId,
    hash,
    generatedAt: snapshot.generatedAt,
    syncedAt,
    snapshot,
  });

  // Rebuild the voucher index wholesale. The snapshot model is deletion-correct
  // by construction (CLAUDE.md §2) and merging would reintroduce exactly the
  // deleted-voucher problem the full-snapshot design exists to avoid.
  const store = tx.objectStore("vouchers");
  const existing = await store.getAllKeys();
  for (const key of existing) {
    const row = await store.get(key);
    if (row?.companyId === companyId) await store.delete(key);
  }
  for (const voucher of snapshot.vouchers) {
    await store.put({ ...voucher, companyId });
  }

  await tx.done;
}

export async function loadSnapshot(companyId: string): Promise<StoredSnapshot | undefined> {
  return (await db()).get("snapshots", companyId);
}

export async function listStoredCompanies(): Promise<StoredSnapshot[]> {
  return (await db()).getAll("snapshots");
}

export async function clearCompany(companyId: string): Promise<void> {
  const database = await db();
  const tx = database.transaction(["snapshots", "vouchers"], "readwrite");
  await tx.objectStore("snapshots").delete(companyId);
  const store = tx.objectStore("vouchers");
  for (const key of await store.getAllKeys()) {
    const row = await store.get(key);
    if (row?.companyId === companyId) await store.delete(key);
  }
  await tx.done;
}

export async function getSetting<T>(key: string, fallback: T): Promise<T> {
  const value = await (await db()).get("settings", key);
  return (value as T | undefined) ?? fallback;
}

export async function setSetting(key: string, value: unknown): Promise<void> {
  await (await db()).put("settings", value, key);
}

export async function deleteSetting(key: string): Promise<void> {
  await (await db()).delete("settings", key);
}

export const SETTINGS = {
  /**
   * The original single-connection keys. Still read on startup so an app that
   * was set up before multi-company keeps working, and still written so a
   * downgrade is not a dead end — but `connections` is the source of truth.
   */
  baseUrl: "baseUrl",
  readToken: "readToken",
  companyId: "companyId",

  /** array of Connection — one entry per company this phone can open */
  connections: "connections",
  /** which of them is on screen */
  activeCompany: "activeCompany",
} as const;
